/**
 * Planned hours against actual hours, by month.
 *
 * The planned figure is DemandPlan.GuidanceQuantity, summed over the people on
 * the demand for that period. It is the only budget field in NOS that varies
 * by month -- Demand.BudgetQuantity is one project-lifetime number repeated on
 * every period row, and charting THAT per month compared a whole project
 * against four weeks twelve times over.
 *
 * Paired bars rather than stacked: the gap between them is the thing being
 * read, not the total. One y-scale, both in hours.
 */
import { useState } from "react";
import {
  ChartFrame,
  EmptyChart,
  GRID,
  Legend,
  niceBounds,
  SERIES_1,
  SERIES_2,
  useChartWidth,
} from "./chart-primitives";

const BASE_W = 720;
const H = 220;
const PAD = { top: 18, right: 16, bottom: 28, left: 52 };

export interface PlannedMonth {
  label: string;
  /** Null when no plan exists for this month -- not zero. */
  plannedHours: number | null;
  actualHours: number;
  id?: string;
}

const hours = (n: number) =>
  n >= 1000 ? `${Math.round(n / 1000).toLocaleString()}k` : `${Math.round(n).toLocaleString()}`;

export function PlannedVsActual({
  months,
  title = "Planned vs actual hours",
  subtitle,
  onSelect,
}: {
  months: PlannedMonth[];
  title?: string;
  subtitle?: string;
  onSelect?: (m: PlannedMonth) => void;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const [frame, W] = useChartWidth(BASE_W);

  // A month with no plan cannot be compared. Drawing a zero-height planned bar
  // would read as "planned nothing, burned 130 hours", which is a different and
  // much worse claim than "nobody planned that month".
  const plotted = months.filter((m) => m.plannedHours !== null);
  if (plotted.length === 0) {
    return (
      <ChartFrame title={title} subtitle={subtitle}>
        <EmptyChart message="No monthly plan on record for this project" />
      </ChartFrame>
    );
  }

  const [lo, hi] = niceBounds(plotted.flatMap((m) => [m.plannedHours as number, m.actualHours]));
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const slot = innerW / plotted.length;
  // 2px of surface between the pair so they read as two marks.
  const barW = Math.max(3, Math.min(18, slot / 2 - 3));
  const y = (v: number) => PAD.top + innerH - ((v - lo) / (hi - lo || 1)) * innerH;
  const ticks = [lo, lo + (hi - lo) / 2, hi];
  const over = plotted.filter((m) => m.actualHours > (m.plannedHours as number));
  const active = hover === null ? null : plotted[hover];
  const skipped = months.length - plotted.length;

  return (
    <ChartFrame
      title={title}
      subtitle={subtitle}
      legend={
        <Legend
          items={[
            { label: "Planned", color: SERIES_2 },
            { label: "Actual", color: SERIES_1 },
          ]}
        />
      }
      footer={
        <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {/* "Over plan" alone sat a card away from a whole-project "31.6%
              under budget" and read as a flat contradiction. Naming the basis
              -- each month against ITS OWN plan -- is what makes the two
              statements sit together. */}
          <span>
            {over.length > 0
              ? `Over the month's own plan in ${over.map((m) => m.label).join(", ")}`
              : "Every planned month came in at or under its own plan."}
          </span>
          {skipped > 0 ? (
            <span className="before:mr-3 before:text-border before:content-['·']">
              {skipped} month{skipped === 1 ? "" : "s"} had no plan and{" "}
              {skipped === 1 ? "is" : "are"} not shown
            </span>
          ) : null}
        </span>
      }
    >
      <div ref={frame} className="relative w-full overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 420 }} role="img">
          <title>{title}</title>
          {ticks.map((t) => (
            <g key={t}>
              <line
                x1={PAD.left}
                x2={W - PAD.right}
                y1={y(t)}
                y2={y(t)}
                stroke={GRID}
                strokeWidth={1}
              />
              <text
                x={PAD.left - 8}
                y={y(t) + 3}
                textAnchor="end"
                className="fill-muted-foreground"
                style={{ fontSize: 10 }}
              >
                {hours(t)}
              </text>
            </g>
          ))}

          {plotted.map((m, i) => {
            const cx = PAD.left + slot * i + slot / 2;
            const base = y(Math.max(lo, 0));
            const planned = m.plannedHours as number;
            const isOver = m.actualHours > planned;
            return (
              <g
                key={m.label}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
                onClick={() => onSelect?.(m)}
                className={onSelect ? "cursor-pointer" : undefined}
                opacity={hover === null || hover === i ? 1 : 0.55}
              >
                <rect
                  x={PAD.left + slot * i}
                  y={PAD.top}
                  width={slot}
                  height={innerH}
                  fill="transparent"
                />
                <rect
                  x={cx - barW - 1}
                  y={y(planned)}
                  width={barW}
                  height={Math.max(1, base - y(planned))}
                  rx={3}
                  fill={SERIES_2}
                />
                <rect
                  x={cx + 1}
                  y={y(m.actualHours)}
                  width={barW}
                  height={Math.max(1, base - y(m.actualHours))}
                  rx={3}
                  fill={SERIES_1}
                />
                {/* Shape, not colour: survives greyscale and colour-blind reading. */}
                {isOver ? (
                  <text
                    x={cx}
                    y={PAD.top - 5}
                    textAnchor="middle"
                    className="fill-muted-foreground"
                    style={{ fontSize: 11 }}
                    aria-label={`${m.label} over plan`}
                  >
                    ▲
                  </text>
                ) : null}
              </g>
            );
          })}

          {plotted.map((m, i) => (
            <text
              key={m.label}
              x={PAD.left + slot * i + slot / 2}
              y={H - 10}
              textAnchor="middle"
              className="fill-muted-foreground"
              style={{ fontSize: 10 }}
            >
              {m.label.split(" ")[0]?.slice(0, 3)}
            </text>
          ))}
        </svg>

        {active ? (
          <div className="pointer-events-none absolute left-2 top-2 rounded-md border border-border bg-popover px-2 py-1 text-xs shadow-sm">
            <div className="font-medium">{active.label}</div>
            <div className="text-muted-foreground">
              Planned {hours(active.plannedHours as number)}h · Actual {hours(active.actualHours)}h
            </div>
          </div>
        ) : null}
      </div>
    </ChartFrame>
  );
}
